import { useCallback, useState } from 'react';

import { InvalidPhone, countryByCode, parsePhoneList } from '@vyce/core/src/utils/phone';

export interface PhoneListResult {
  added: number;
  /** Repeated inside the pasted batch or already in the list */
  duplicates: number;
  skippedOverLimit: number;
  /** Pasted lines with no digits at all — names, column headers */
  ignored: number;
  invalid: InvalidPhone[];
  usedDefaultCountry: boolean;
  defaultCountryName: string;
  defaultDialCode: string;
}

interface Props {
  existing: string[];
  onAdd: (phones: string[]) => void;
  limit?: number;
}

/**
 * Turns a pasted list of numbers into contacts. Knows nothing about invites or forms, so
 * both flows report the same counts and the same reasons.
 */
export const usePhoneListPaste = ({ existing, onAdd, limit }: Props) => {
  const [result, setResult] = useState<PhoneListResult | null>(null);

  const clear = useCallback(() => setResult(null), []);

  const handlePasteList = useCallback(
    (text: string, defaultCountryCode: string) => {
      const parsed = parsePhoneList(text, defaultCountryCode);
      if (!parsed.valid.length && !parsed.invalid.length && !parsed.ignored) {
        return false;
      }

      const fresh = parsed.valid.filter(phone => !existing.includes(phone));
      const alreadyAdded = parsed.valid.length - fresh.length;

      // Cap the batch rather than letting the list run past the limit — otherwise the only
      // way back is deleting chips one at a time
      const room = limit === undefined ? fresh.length : Math.max(limit - existing.length, 0);
      const toAdd = fresh.slice(0, room);

      if (toAdd.length) {
        onAdd(toAdd);
      }

      const country = countryByCode(defaultCountryCode);
      setResult({
        added: toAdd.length,
        duplicates: parsed.duplicatesInBatch + alreadyAdded,
        skippedOverLimit: fresh.length - toAdd.length,
        ignored: parsed.ignored,
        invalid: parsed.invalid,
        usedDefaultCountry: parsed.usedDefaultCountry,
        defaultCountryName: country.name,
        defaultDialCode: country.dialCode,
      });

      return true;
    },
    [existing, onAdd, limit]
  );

  return { result, handlePasteList, dismiss: clear, clear };
};

export type PhoneListPasteApi = ReturnType<typeof usePhoneListPaste>;
